import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Image from "../../../components/AppImage";
import ImageSourceDropdown from "./ImageSourceDropdown";

const QRCodeLinking = ({ qrCode, location, onQRCodeChange, onCaptureLocation, errors }) => {
  const [locationStatus, setLocationStatus] = useState("pending");
  const [coordinates, setCoordinates] = useState(null);
  const [isProcessingQr, setIsProcessingQr] = useState(false);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);

  useEffect(() => {
    if (location?.error) {
      setLocationStatus("error");
      setCoordinates(null);
    } else if (location?.verified && location?.coordinates) {
      setLocationStatus("verified");
      setCoordinates(location.coordinates);
    } else if (location?.coordinates) {
      // If coordinates are set but not explicitly verified (auto-captured), show as verified
      setLocationStatus("verified");
      setCoordinates(location.coordinates);
    } else {
      setLocationStatus("pending");
      setCoordinates(null);
    }
  }, [location?.verified, location?.coordinates, location?.error]);

  const handleFileSelect = (file) => {
    if (!file?.type?.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }
    setIsProcessingQr(true);
    try {
      onQRCodeChange(file);
    } finally {
      setIsProcessingQr(false);
    }
  };

  const handleRemoveImage = () => onQRCodeChange("");

  // Get image URL for preview
  const getImageUrl = () => {
    if (!qrCode) return null;
    if (qrCode instanceof File) {
      return URL.createObjectURL(qrCode);
    }
    return qrCode; // Assume it's a URL string
  };

  const imageUrl = getImageUrl();

  // Cleanup object URL on unmount or when qrCode changes
  useEffect(() => {
    return () => {
      if (qrCode instanceof File && imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [qrCode, imageUrl]);

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <span className="text-lg font-semibold text-primary">4</span>
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          QR Code & Location
        </h2>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            QR Code Sticker Image
          </label>

          {!imageUrl ? (
            <div className="space-y-3">
              <ImageSourceDropdown
                idPrefix="qr-code-upload"
                triggerLabel="Add QR Code Sticker Image"
                triggerSubLabel="Upload or capture"
                processing={isProcessingQr}
                onFileSelect={handleFileSelect}
              />
              <p className="text-xs text-muted-foreground text-center">
                Upload or capture an image of the QR code sticker
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={20} className="text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-success mb-1">
                    QR Code Image Uploaded
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {qrCode instanceof File ? qrCode.name : "Image uploaded"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={handleRemoveImage}
                >
                  Remove
                </Button>
              </div>
              <div className="rounded-lg overflow-hidden border border-border bg-muted">
                <div className="aspect-square max-h-64 flex items-center justify-center">
                  <Image
                    src={imageUrl}
                    alt="QR Code Sticker"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}
          {errors?.qrCode && (
            <p className="text-sm text-error">{errors?.qrCode}</p>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Geo-Location Verification
          </label>

          {locationStatus === "verified" && coordinates && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <Icon name="MapPinCheck" size={20} className="text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-success mb-1">
                    Location Captured
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Accuracy: ±{Math.round(coordinates?.accuracy)}m
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Latitude"
                  type="text"
                  value={coordinates?.latitude?.toFixed(6)}
                  disabled
                  className="w-full"
                />
                <Input
                  label="Longitude"
                  type="text"
                  value={coordinates?.longitude?.toFixed(6)}
                  disabled
                  className="w-full"
                />
              </div>
            </div>
          )}

          {locationStatus === "pending" && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 bg-muted/50 border border-border rounded-lg">
              <Icon name="MapPin" size={20} className="text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  Capture your current location
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click Capture to get GPS coordinates before submitting the survey
                </p>
              </div>
              <Button
                type="button"
                onClick={async () => {
                  if (!onCaptureLocation) return;
                  setIsCapturingLocation(true);
                  try {
                    await onCaptureLocation();
                  } finally {
                    setIsCapturingLocation(false);
                  }
                }}
                disabled={isCapturingLocation}
                loading={isCapturingLocation}
                iconName="MapPin"
                className="flex-shrink-0"
              >
                Capture
              </Button>
            </div>
          )}

          {locationStatus === "error" && (
            <div className="flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error">
                  Location Access Denied
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Please enable location services and try submitting again
                </p>
              </div>
            </div>
          )}

          {errors?.location && (
            <p className="text-sm text-error">{errors?.location}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeLinking;
